import type { BlockerFunction } from 'react-router-dom'
import { useCallback, useState } from 'react'
import { Button } from '@/components/ui/button'
import { DialogPattern } from '@/components/ui/dialog-pattern'
import { CircleNotchIcon } from '@phosphor-icons/react'
import { useBeforeUnload, useBlocker } from 'react-router-dom'

interface UnsavedChangesGuardProps {
  /** Block in-app navigation and page unload while true. */
  when: boolean
  /**
   * Persist the pending changes; navigation proceeds after it resolves. A
   * save that resolves `false` or throws keeps the user on the page.
   */
  onSave: () => unknown
  /** Drop the pending changes before navigation proceeds. */
  onDiscard?: () => void
  isSaving?: boolean
  /** Noun used in the dialog copy, e.g. "ticket type". */
  noun?: string
  /**
   * Narrows which in-app navigations ask first (only while `when`). Omit to
   * block every navigation; pass a predicate when some navigations keep the
   * draft mounted (e.g. a `?section=` switch on a page-level draft).
   */
  shouldBlock?: BlockerFunction
}

/**
 * Page-scope unsaved-changes guard: blocks route changes and browser unload
 * while `when` is true, offering Save / Discard / Cancel. Use one per settings
 * surface that buffers edits (paired with a StickySaveBar).
 */
export function UnsavedChangesGuard({
  when,
  onSave,
  onDiscard,
  isSaving = false,
  noun = 'setting',
  shouldBlock,
}: UnsavedChangesGuardProps) {
  const blocker = useBlocker(
    useCallback<BlockerFunction>(
      args => when && (shouldBlock ? shouldBlock(args) : true),
      [when, shouldBlock],
    ),
  )
  const [pendingSave, setPendingSave] = useState(false)

  useBeforeUnload(
    useCallback(
      (event: BeforeUnloadEvent) => {
        if (!when) return
        event.preventDefault()
        event.returnValue = ''
      },
      [when],
    ),
  )

  const close = () => {
    if (blocker.state === 'blocked') blocker.reset()
  }

  const discardAndContinue = () => {
    onDiscard?.()
    if (blocker.state === 'blocked') blocker.proceed()
  }

  const saveAndContinue = async () => {
    setPendingSave(true)
    let saved = false
    try {
      saved = (await onSave()) !== false
    } catch {
      // The save reports its own failure; stay so nothing is lost.
    } finally {
      setPendingSave(false)
    }
    if (blocker.state !== 'blocked') return
    if (saved) blocker.proceed()
    else blocker.reset()
  }

  const busy = pendingSave || isSaving

  return (
    <DialogPattern
      open={blocker.state === 'blocked'}
      onOpenChange={open => {
        if (!open) close()
      }}
      title="Unsaved changes"
      description={`You have unsaved ${noun} changes. Save them before leaving, or discard and continue.`}
      contentClassName="py-0"
      footer={
        <>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={close}
            disabled={busy}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            size="sm"
            onClick={discardAndContinue}
            disabled={busy}
          >
            Discard
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={() => void saveAndContinue()}
            disabled={busy}
            className="min-w-[100px]"
          >
            {busy ? (
              <>
                <CircleNotchIcon className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                Saving…
              </>
            ) : (
              'Save'
            )}
          </Button>
        </>
      }
    >
      {null}
    </DialogPattern>
  )
}
